import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { companyInfo, navLinks } from "@/data/mockData";
import {
  Sun,
  Moon,
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  FileText,
  ArrowUp,
} from "lucide-react";
import QuoteModal from "@/components/QuoteModal";

export default function Layout({ children }) {
  const { isDark, toggleTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const handleNavClick = (e, path) => {
    if (path.startsWith("/#")) {
      e.preventDefault();
      const id = path.replace("/#", "");
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const isHome = location.pathname === "/";
  const showSolid = scrolled || !isHome;

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-900" : "bg-white"}`}>
      {/* Navbar */}
      <nav
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          showSolid
            ? isDark
              ? "bg-slate-900/95 backdrop-blur-md shadow-lg shadow-black/20"
              : "bg-white/95 backdrop-blur-md shadow-lg shadow-gray-200/50"
            : "bg-transparent"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group" data-testid="logo-link">
              <div className="h-14 w-36 overflow-hidden rounded">
                <img
                  src={companyInfo.logo}
                  alt="Mehra Mac Industries"
                  className="h-full w-full object-cover scale-[2.2]"
                  data-testid="header-logo"
                />
              </div>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`text-sm font-medium transition-colors hover:text-amber-500 ${
                    isDark ? "text-gray-300" : showSolid ? "text-slate-700" : "text-gray-200"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button
                onClick={toggleTheme}
                data-testid="theme-toggle-btn"
                className={`p-2 rounded-lg transition-all duration-300 ${
                  isDark
                    ? "bg-slate-800 text-amber-400 hover:bg-slate-700"
                    : "bg-gray-100 text-amber-600 hover:bg-gray-200"
                }`}
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setShowQuote(true)}
                data-testid="get-quote-btn"
                className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
              >
                Get Quote
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center space-x-3">
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${
                  isDark
                    ? "bg-slate-800 text-amber-400"
                    : "bg-gray-100 text-amber-600"
                }`}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`p-2 ${isDark ? "text-white" : showSolid ? "text-slate-900" : "text-white"}`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div
              className={`md:hidden pb-6 border-t ${
                isDark ? "border-slate-800 bg-slate-900" : "border-gray-200 bg-white"
              }`}
            >
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`block py-3 px-4 text-sm font-medium ${
                    isDark
                      ? "text-gray-300 hover:text-amber-400"
                      : "text-slate-700 hover:text-amber-600"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="px-4 pt-2">
                <button
                  onClick={() => { setShowQuote(true); setMobileMenuOpen(false); }}
                  className="w-full px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-sm font-semibold rounded-lg"
                >
                  Get Quote
                </button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer
        className={`py-16 ${
          isDark ? "bg-slate-950" : "bg-gray-900"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="h-12 w-32 overflow-hidden rounded bg-white/90">
                  <img
                    src={companyInfo.logo}
                    alt="Mehra Mac Industries"
                    className="h-full w-full object-cover scale-[2.2]"
                  />
                </div>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Leading copper wire manufacturer in Hyderabad. Quality products for electrical, submersible pump, and wire drawing industries.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.path}
                      onClick={(e) => handleNavClick(e, link.path)}
                      className="text-gray-400 text-sm hover:text-amber-400 transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Products */}
            <div>
              <h3 className="text-white font-semibold mb-4">Our Products</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Submersible Wires (PolyMac)</li>
                <li>Bare Copper Wires</li>
                <li>Earthing Wire</li>
                <li>CCR Wires</li>
                <li>Annealed Copper Wire</li>
                <li>Copper Rods</li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-white font-semibold mb-4">Contact Us</h3>
              <div className="space-y-3">
                <div className="text-gray-400 text-sm">
                  <Phone className="w-4 h-4 text-amber-400 inline mr-2" />
                  <span className="font-medium text-gray-300">Unit Manager:</span>
                  <a href="tel:8341019995" className="ml-1 hover:text-amber-400 transition-colors">8341019995</a>
                </div>
                <div className="text-gray-400 text-sm">
                  <Phone className="w-4 h-4 text-amber-400 inline mr-2" />
                  <span className="font-medium text-gray-300">Sales:</span>
                  <a href="tel:9000999995" className="ml-1 hover:text-amber-400 transition-colors">9000999995</a>
                  <span className="mx-1">/</span>
                  <a href="tel:9246523153" className="hover:text-amber-400 transition-colors">9246523153</a>
                </div>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center space-x-3 text-gray-400 text-sm hover:text-amber-400 transition-colors"
                >
                  <Mail className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <span>{companyInfo.email}</span>
                </a>
                <div className="flex items-start space-x-3 text-gray-400 text-sm">
                  <MapPin className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span>Hyderabad, Telangana</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-gray-500 text-sm">
              &copy; {new Date().getFullYear()} Mehra Mac Industries. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={`https://wa.me/91${companyInfo.whatsapp}?text=${encodeURIComponent("Hi, I am interested in your copper wire products. Can you provide more information?")}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 group"
        aria-label="Contact us on WhatsApp"
        data-testid="whatsapp-btn"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-[#25D366] rounded-full animate-ping opacity-75" />
          <div className="relative flex items-center justify-center w-16 h-16 bg-[#25D366] hover:bg-[#20BA5C] rounded-full shadow-2xl transition-all duration-300 group-hover:scale-110">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-white fill-current">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
          <div className="absolute right-20 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <div className={`${isDark ? "bg-slate-900" : "bg-slate-800"} text-white px-4 py-2 rounded-lg shadow-xl whitespace-nowrap text-sm font-medium`}>
              Chat on WhatsApp
            </div>
          </div>
        </div>
      </a>

      {/* Floating Quote Button */}
      <button
        onClick={() => setShowQuote(true)}
        className="fixed bottom-24 right-6 z-40 group"
        data-testid="floating-quote-btn"
      >
        <div className="relative">
          <div className="relative flex items-center justify-center w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 rounded-full shadow-2xl transition-all duration-300 group-hover:scale-110">
            <FileText className="w-7 h-7 text-white" />
          </div>
          <div className="absolute right-18 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
            <div className={`${isDark ? "bg-slate-900" : "bg-slate-800"} text-white px-4 py-2 rounded-lg shadow-xl whitespace-nowrap text-sm font-medium`}>
              Quick Quote Request
            </div>
          </div>
        </div>
      </button>

      {/* Scroll to Top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`fixed bottom-44 right-7 z-40 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 ${
            isDark
              ? "bg-slate-800 text-amber-400 hover:bg-slate-700"
              : "bg-white text-amber-600 hover:bg-gray-50 border border-gray-200"
          }`}
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}

      {/* Quote Modal */}
      <QuoteModal open={showQuote} onClose={() => setShowQuote(false)} />
    </div>
  );
}
